import { useState } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { SyncErrorDialog, describeSyncError } from './sync-error-dialog';
import {
  ArrowDownToLine,
  ArrowUpFromLine,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  RefreshCw,
  Info,
} from 'lucide-react';
import type { SyncLog } from '../types/integration.types';

interface Props {
  logs: SyncLog[];
  showIntegration?: boolean;
}

const STATUS_BADGE: Record<
  string,
  { variant: 'default' | 'secondary' | 'destructive' | 'outline'; label: string; icon: any }
> = {
  SUCCESS: { variant: 'default', label: 'Exitoso', icon: CheckCircle2 },
  PARTIAL: { variant: 'secondary', label: 'Parcial', icon: AlertTriangle },
  FAILED: { variant: 'destructive', label: 'Fallido', icon: XCircle },
  RUNNING: { variant: 'outline', label: 'En curso', icon: RefreshCw },
};

export function SyncLogTable({ logs, showIntegration = true }: Props) {
  const [detailLog, setDetailLog] = useState<SyncLog | null>(null);

  if (logs.length === 0) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        No hay registros de sincronización
      </div>
    );
  }

  return (
    <>
      <Table>
        <TableHeader>
          <TableRow>
            {showIntegration && <TableHead>Integración</TableHead>}
            <TableHead>Dirección</TableHead>
            <TableHead>Estado</TableHead>
            <TableHead className="text-right">Total</TableHead>
            <TableHead className="text-right">Exitosos</TableHead>
            <TableHead className="text-right">Fallidos</TableHead>
            <TableHead className="text-right">Omitidos</TableHead>
            <TableHead>Inicio</TableHead>
            <TableHead>Disparado por</TableHead>
            <TableHead>Detalle</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {logs.map((log) => {
            const statusInfo = STATUS_BADGE[log.status] || STATUS_BADGE.FAILED;
            const StatusIcon = statusInfo.icon;

            return (
              <TableRow key={log.id}>
                {showIntegration && (
                  <TableCell className="font-medium">{log.integration?.name || '\u2014'}</TableCell>
                )}
                <TableCell>
                  <div className="flex items-center gap-1">
                    {log.direction === 'INBOUND' ? (
                      <ArrowDownToLine className="h-3.5 w-3.5 text-blue-500" />
                    ) : (
                      <ArrowUpFromLine className="h-3.5 w-3.5 text-orange-500" />
                    )}
                    {log.direction === 'INBOUND' ? 'Importacion' : 'Exportacion'}
                  </div>
                </TableCell>
                <TableCell>
                  <Badge variant={statusInfo.variant} className="gap-1">
                    <StatusIcon className="h-3 w-3" />
                    {statusInfo.label}
                  </Badge>
                </TableCell>
                <TableCell className="text-right">{log.totalRecords}</TableCell>
                <TableCell className="text-right text-green-500">{log.successCount}</TableCell>
                <TableCell className="text-right text-red-500">{log.failedCount}</TableCell>
                <TableCell className="text-right text-muted-foreground">
                  {log.skippedCount}
                </TableCell>
                <TableCell className="text-sm text-muted-foreground">
                  {new Date(log.startedAt).toLocaleString('es')}
                </TableCell>
                <TableCell className="text-sm text-muted-foreground">
                  {log.triggeredBy === 'scheduled' ? 'Programado' : 'Manual'}
                </TableCell>
                <TableCell className="max-w-[320px]">
                  {log.errorDetails ? (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7 gap-1 px-2 text-left text-red-600 dark:text-red-400 max-w-full"
                      onClick={() => setDetailLog(log)}
                      title="Ver detalle del error"
                    >
                      <Info className="h-3.5 w-3.5 shrink-0" />
                      <span className="truncate text-xs">
                        {describeSyncError(log.errorDetails).message}
                      </span>
                    </Button>
                  ) : (
                    <span className="text-muted-foreground">{'\u2014'}</span>
                  )}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
      <SyncErrorDialog log={detailLog} onClose={() => setDetailLog(null)} />
    </>
  );
}
